/**
 * Bulk Data Upload Component
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  SelectChangeEvent,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  Undo as UndoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { ImportResult, ImportHistory, MappingTemplate } from '../../types/integration';

const DATA_TYPES = [
  { value: 'products', label: 'Products' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'sales', label: 'Sales Transactions' },
  { value: 'stores', label: 'Stores' },
];

const FILE_FORMATS = [
  { value: 'csv', label: 'CSV', accept: '.csv' },
  { value: 'json', label: 'JSON', accept: '.json' },
  { value: 'xml', label: 'XML', accept: '.xml' },
];

const DataUpload: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dataType, setDataType] = useState('products');
  const [fileFormat, setFileFormat] = useState('csv');
  const [mappingTemplateId, setMappingTemplateId] = useState('');
  const [validateOnly, setValidateOnly] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);

  // Fetch mapping templates
  const { data: templates } = useQuery<MappingTemplate[]>({
    queryKey: ['mappingTemplates'],
    queryFn: async () => {
      const response = await api.getMappingTemplates();
      return response.data;
    },
  });

  // Fetch import history
  const { data: history, refetch: refetchHistory } = useQuery<ImportHistory[]>({
    queryKey: ['importHistory'],
    queryFn: async () => {
      const response = await api.getImportHistory(50);
      return response.data;
    },
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      if (fileFormat === 'csv') {
        return api.importCSV(file, dataType, mappingTemplateId || undefined, validateOnly);
      } else if (fileFormat === 'json') {
        return api.importJSON(file, dataType, validateOnly);
      } else if (fileFormat === 'xml') {
        return api.importXML(file, dataType);
      }
      throw new Error('Unsupported file format');
    },
    onSuccess: (response) => {
      setImportResult(response.data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
      refetchHistory();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Import failed');
      setImportResult(null);
    },
  });

  // Rollback mutation
  const rollbackMutation = useMutation({
    mutationFn: (importId: string) => api.rollbackImport(importId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['importHistory'] });
      refetchHistory();
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Rollback failed');
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
      setError(null);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    importMutation.mutate(selectedFile);
  };

  const handleRollback = (importId: string) => {
    if (window.confirm('Are you sure you want to rollback this import? This will delete all imported data.')) {
      rollbackMutation.mutate(importId);
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'COMPLETED':
      case 'VALIDATED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'ROLLED_BACK':
        return 'warning';
      default:
        return 'info';
    }
  };

  const filteredTemplates = templates?.filter(t => t.data_type === dataType) || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <UploadIcon sx={{ fontSize: 40, mr: 2 }} />
        <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>Bulk Data Upload</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Upload Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload File
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {importResult && (
              <Alert
                severity={importResult.status === 'FAILED' ? 'error' : 'success'}
                sx={{ mb: 2 }}
                onClose={() => setImportResult(null)}
              >
                <Typography variant="subtitle2">
                  Import {importResult.status}
                </Typography>
                <Typography variant="body2">
                  Total Rows: {importResult.total_rows} |
                  {importResult.imported_rows !== undefined && ` Imported: ${importResult.imported_rows} |`}
                  {importResult.valid_rows !== undefined && ` Valid: ${importResult.valid_rows}`}
                </Typography>
                {importResult.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="error">
                      {importResult.errors.slice(0, 5).join(', ')}
                      {importResult.errors.length > 5 && ` ... and ${importResult.errors.length - 5} more errors`}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Data Type</InputLabel>
                <Select
                  value={dataType}
                  label="Data Type"
                  onChange={(e: SelectChangeEvent) => {
                    setDataType(e.target.value);
                    setMappingTemplateId('');
                  }}
                >
                  {DATA_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>File Format</InputLabel>
                <Select
                  value={fileFormat}
                  label="File Format"
                  onChange={(e: SelectChangeEvent) => setFileFormat(e.target.value)}
                >
                  {FILE_FORMATS.map((format) => (
                    <MenuItem key={format.value} value={format.value}>
                      {format.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {fileFormat === 'csv' && filteredTemplates.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Mapping Template (Optional)</InputLabel>
                  <Select
                    value={mappingTemplateId}
                    label="Mapping Template (Optional)"
                    onChange={(e: SelectChangeEvent) => setMappingTemplateId(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {filteredTemplates.map((template) => (
                      <MenuItem key={template.template_id} value={template.template_id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box>
                <input
                  accept={FILE_FORMATS.find(f => f.value === fileFormat)?.accept}
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<UploadIcon />}
                  >
                    Choose File
                  </Button>
                </label>
                {selectedFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleUpload}
                  disabled={!selectedFile || importMutation.isPending}
                  startIcon={importMutation.isPending ? <CircularProgress size={20} /> : <UploadIcon />}
                >
                  {validateOnly ? 'Validate Only' : 'Upload & Import'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setValidateOnly(!validateOnly)}
                  color={validateOnly ? 'warning' : 'primary'}
                >
                  {validateOnly ? 'Validate Mode' : 'Import Mode'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Import Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <SuccessIcon color="success" sx={{ fontSize: 40 }} />
                    <Typography variant="h4">
                      {history?.filter(h => h.status === 'COMPLETED').length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Successful Imports
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <ErrorIcon color="error" sx={{ fontSize: 40 }} />
                    <Typography variant="h4">
                      {history?.filter(h => h.status === 'FAILED').length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Failed Imports
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Rows Imported
                    </Typography>
                    <Typography variant="h5">
                      {history?.reduce((sum, h) => sum + (h.imported_rows || 0), 0) || 0}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Import History */}
      <Paper sx={{ mt: 3, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HistoryIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Import History</Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Data Type</strong></TableCell>
                <TableCell><strong>Import Status</strong></TableCell>
                <TableCell align="right"><strong>Total Rows</strong></TableCell>
                <TableCell align="right"><strong>Imported</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history && history.length > 0 ? (
                history.map((record) => (
                  <React.Fragment key={record.import_id}>
                    <TableRow hover>
                      <TableCell>{new Date(record.created_at).toLocaleString()}</TableCell>
                      <TableCell>{record.data_type}</TableCell>
                      <TableCell>
                        <Chip
                          label={record.status}
                          color={getStatusColor(record.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{record.total_rows}</TableCell>
                      <TableCell align="right">{record.imported_rows}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {record.errors.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedHistoryId(
                                  expandedHistoryId === record.import_id ? null : record.import_id
                                )
                              }
                            >
                              {expandedHistoryId === record.import_id ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          )}
                          {record.status === 'COMPLETED' && !record.rolled_back_at && (
                            <IconButton
                              size="small"
                              onClick={() => handleRollback(record.import_id)}
                              color="warning"
                              title="Rollback"
                            >
                              <UndoIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {record.errors.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0 }}>
                          <Collapse in={expandedHistoryId === record.import_id}>
                            <Box sx={{ py: 2 }}>
                              <Alert severity="error" icon={<WarningIcon />}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Errors ({record.errors.length})
                                </Typography>
                                <List dense>
                                  {record.errors.slice(0, 10).map((err, idx) => (
                                    <ListItem key={idx}>
                                      <ListItemText
                                        primary={err}
                                        primaryTypographyProps={{ variant: 'caption' }}
                                      />
                                    </ListItem>
                                  ))}
                                  {record.errors.length > 10 && (
                                    <ListItem>
                                      <ListItemText
                                        primary={`... and ${record.errors.length - 10} more errors`}
                                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                      />
                                    </ListItem>
                                  )}
                                </List>
                              </Alert>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No import history found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DataUpload;
